import React, {Component, Suspense} from "react"

import dayjs from "dayjs"
import PropTypes from "prop-types"
import {connect} from "react-redux"
import {Route, Routes} from "react-router-dom"

import {AREA_TYPE} from "../constants/areaType"
import {menuContent} from "../constants/menu/menu"
import {Page} from "../constants/page/pages"
import {PagesArchitecture} from "../constants/page/pagesArchitecture"
import {saveCookiesChoices} from "../shared-components/actions/localStorage/cookies/cookiesActions"
import {disconnectUser} from "../shared-components/actions/sessionStorage/user/userActions"
import {clearCheckbooks} from "../shared-components/actions/temporaries/handOverCheckbooks/handOverCheckbooksActions"
import {setResponsive} from "../shared-components/actions/temporaries/menu/menuAction"
import {logoutUser} from "../shared-components/common/actions/user/userActions"
import CookiesModal from "../shared-components/components/CookiesModal"
import Footer from "../shared-components/components/Footer/footer"
import MenuContainer from "../shared-components/components/Menu/menu"
import MenuTop from "../shared-components/components/MenuTop/menuTop"
import {DOMISERVE_CONTACT_URL, DOMISERVE_DONNEES_PERSONNELLES_URL} from "../shared-components/constants/links"
import {showCookiesModal} from "../store/selectors/localStorage/cookiesSelectors"
import {getUserFullname, getUserPermissions, isUserConnected} from "../store/selectors/sessionStorage/userSelectors"
import {isMenuResponsive} from "../store/selectors/temporaries/menuSelectors"
import {getActualPage} from "../store/selectors/temporaries/pageSelectors"
import {AsyncBreadcrumbLayout, AsyncUnauthorizedScene} from "./asyncScenes"
import {MatomoTrackPageView} from "./matomoTrackPageView"

import "./mainLayout.scss"
import withRouter from "../shared-components/wrappers/withRouter"

class MainLayout extends Component {
    getBreadcrumbLayout = () => (
        <AsyncBreadcrumbLayout actualPage={this.props.actualPage} isUserConnected={this.props.isUserConnected} />
    )

    onDisconnectClick = () => {
        this.props.logoutUser().catch(err => console.error(err))
        this.props.disconnectUser()
    }

    render() {
        const {
            actualPage,
            router,
            isCookiesModalOpened,
            isMenuResponsive,
            isUserConnected,
            onConnectClick,
            onContactClick,
            onCookiesClick,
            onCookiesClose,
            onHomeClick,
            onLegalClick,
            onProfileClick,
            onRgpdClick,
            setMenuResponsive,
            username,
            userPermissions,
        } = this.props

        return (
            <div
                className={
                    "main-layout" +
                    (process.env.NODE_ENV === "production" && !process.env.REACT_APP_PROD_MODE
                        ? " main-layout-bordered"
                        : "")
                }
            >
                <MenuTop
                    isConnected={isUserConnected}
                    onConnectClick={onConnectClick}
                    onDisconnectClick={this.onDisconnectClick}
                    onProfileClick={onProfileClick}
                    username={username}
                />
                <MatomoTrackPageView />
                <div className="main-page">
                    <div className="menu-container">
                        <MenuContainer
                            areaType={AREA_TYPE}
                            isConnected={isUserConnected}
                            isMenuResponsive={isMenuResponsive}
                            menuContent={menuContent(actualPage, router.navigate)}
                            onConnectClick={onConnectClick}
                            onDisconnectClick={this.onDisconnectClick}
                            onHomeClick={onHomeClick}
                            onProfileClick={onProfileClick}
                            setMenuResponsive={setMenuResponsive}
                            title="ESPACE INTERVENANT"
                            userPermissions={userPermissions}
                        />
                    </div>
                    <div className="main-content">
                        <Suspense fallback={<div />}>
                            <Routes>
                                {/* Unauthorized route. */}
                                <Route
                                    element={<AsyncUnauthorizedScene />}
                                    path={PagesArchitecture[Page.UNAUTHORIZED].route}
                                />

                                {/* Breadcrumb layout */}
                                <Route element={this.getBreadcrumbLayout()} path="/*" />
                            </Routes>
                        </Suspense>
                    </div>
                </div>
                <CookiesModal isOpen={isCookiesModalOpened} onClose={onCookiesClose} />
                <Footer
                    onContactClick={onContactClick}
                    onCookiesClick={onCookiesClick}
                    onLegalClick={onLegalClick}
                    onRgpdClick={onRgpdClick}
                />
            </div>
        )
    }
}

MainLayout.propsTypes = {
    actualPage: PropTypes.string.isRequired,
    disconnectUser: PropTypes.func.isRequired,
    isCookiesModalOpened: PropTypes.bool.isRequired,
    isMenuResponsive: PropTypes.bool.isRequired,
    logoutUser: PropTypes.func.isRequired,
    onConnectClick: PropTypes.func.isRequired,
    onContactClick: PropTypes.func.isRequired,
    onCookiesClick: PropTypes.func.isRequired,
    onCookiesClose: PropTypes.func.isRequired,
    onHomeClick: PropTypes.func.isRequired,
    onLegalClick: PropTypes.func.isRequired,
    onProfileClick: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
    actualPage: getActualPage(state),
    isMenuResponsive: isMenuResponsive(state),
    isUserConnected: isUserConnected(state),
    username: getUserFullname(state),
    userPermissions: getUserPermissions(state),
    isCookiesModalOpened: showCookiesModal(state),
})

const mapDispatchToProps = (dispatch, props) => ({
    // Action lors de la fermeture de la modale des cookies
    onCookiesClose: () =>
        dispatch(
            saveCookiesChoices({
                isModalOpened: false,
                expirationDate: dayjs().add(6, "month"),
                enabled: true,
            }),
        ),

    // Go to the contact page.
    onContactClick: () => window.open(DOMISERVE_CONTACT_URL),

    // Display cookies modal
    onCookiesClick: () => dispatch(saveCookiesChoices({isModalOpened: true})),

    // Go to the connection page.
    onConnectClick: () => props.router.navigate(PagesArchitecture[Page.LOGIN].route),

    // Disconnect the current user.
    disconnectUser: () =>
        dispatch(disconnectUser()).then(() => {
            dispatch(clearCheckbooks())
            props.router.navigate(PagesArchitecture[Page.LOGIN].route)
        }),
    logoutUser: () => dispatch(logoutUser()),

    // Go to the home page.
    onHomeClick: () => {
        props.router.navigate(PagesArchitecture[Page.HOME].route)
    },
    // Go to the legal mentions page.
    onLegalClick: () => props.router.navigate(PagesArchitecture[Page.LEGAL_NOTICE].route),

    // Go to the rgpd page.
    onRgpdClick: () => window.open(DOMISERVE_DONNEES_PERSONNELLES_URL),

    // Go to the profile page.
    onProfileClick: () => {
        props.router.navigate(PagesArchitecture[Page.PROFILE_MAIN_INFORMATIONS].route)
    },

    setMenuResponsive: isResponsive => {
        dispatch(setResponsive(isResponsive))
    },
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MainLayout))
